import { find, isEmpty, mergeWith, sortBy } from 'lodash';
import { isArray } from 'util';

import { getUserProfileFilterCriteria, UserProfileCriteriaKey } from '../declarations/filter/filter';
import { getStrictExpressions } from '../service/FilterHelpers/FilterHelpers';
import { orderByTypeUserProfileConditions } from '../service/UserProfileHelpers/UserProfileHelpers';
import { CryptaSegment } from '../typings/cryptaSegments';
import { ClientData } from '../typings/domain';
import { FilterExpression, FilterPartialExpression, StatsQueryFilter, UserProfileQueryFilter } from '../typings/filter';
import {
    CryptaInterestTypeWeb,
    GdGoalType,
    UsersProfileQuery_client_goals_rowset,
    UsersProfileQuery_client_retargetings_rowset,
    UsersProfileQuery_client_retargetings_rowset_retargetingCondition_conditionRules,
    UsersProfileQuery_cryptaSegments_rowset
} from '../typings/graphql-grid';
import { GridSourceParams } from '../typings/grid';
import {
    UserProfile,
    UserProfileCondition,
    UserProfileRetargetingConditionGoalType
} from '../typings/userProfileRetargeting';

import { mapStatsDataToStats } from './common/common';
import { FilterCriteriaMappings, textToUserProfileFilterExpression } from './filter/filter';
import { mapRowsToGroupsTruncatedBaseInfo } from './group/group';
import {
    getGoalsMap,
    getIsPriceContextEnabled,
    mapRetargetingConditionRulesForGridRowFromApiFormat
} from './retargeting/retargeting';

const mappings = new FilterCriteriaMappings<UserProfileQueryFilter & StatsQueryFilter>();
const { toNumber } = mappings.valueMappings;

const userProfileCriteriaMappings: {
    [key in UserProfileCriteriaKey]: (expression: FilterExpression) => UserProfileQueryFilter;
} = {
    name: mappings.contains('nameContains', 'nameNotContains'),
    price: mappings.comparison('minPriceContext', 'maxPriceContext', toNumber, false),
    cost: mappings.comparison('minCost', 'maxCost', toNumber),
    shows: mappings.comparison('minShows', 'maxShows', toNumber),
    avgCpmCost: mappings.comparison('minCpmPrice', 'maxCpmPrice', toNumber),
    avgClickCost: mappings.comparison('minAvgClickCost', 'maxAvgClickCost', toNumber, true),
    clicks: mappings.comparison('minClicks', 'maxClicks', toNumber),
    ctr: mappings.comparison('minCtr', 'maxCtr', toNumber),
    goals: mappings.comparison('minGoals', 'maxGoals', toNumber),
    conversionRate: mappings.comparison('minConversionRate', 'maxConversionRate', toNumber),
    bounceRate: mappings.comparison('minBounceRate', 'maxBounceRate', toNumber)
};

const expressionToUserProfileQueryFilter = (expression: FilterExpression): UserProfileQueryFilter => {
    return userProfileCriteriaMappings[expression.criteriaKey](expression);
};

export const mapUserProfileFilterToApiFormat = (expressions?: FilterPartialExpression[]): UserProfileQueryFilter => {
    expressions = expressions || [];
    const criteria = getUserProfileFilterCriteria();

    return getStrictExpressions(expressions, criteria, textToUserProfileFilterExpression)
        .map(expressionToUserProfileQueryFilter)
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {}
        );
};

export function mapRowsToUsersProfile(
    usersProfileRows: UsersProfileQuery_client_retargetings_rowset[],
    cryptaSegments: UsersProfileQuery_cryptaSegments_rowset[],
    goalsFromApi: UsersProfileQuery_client_goals_rowset[],
    clientInfo: ClientData,
    params: GridSourceParams
): Array<UserProfile> {
    const goalsMap = getGoalsMap(goalsFromApi);

    return usersProfileRows.reduce((usersProfiles: UserProfile[], usersProfile) => {
        const {
            retargetingCondition,
            retargetingId,
            adGroup: {
                campaign: { strategy }
            }
        } = usersProfile;

        if (!retargetingCondition) {
            return usersProfiles;
        }

        const { conditionRules } = retargetingCondition;
        const apiConditionRules = getGoalsMetrikaOrAudience(conditionRules);
        const {
            adGroup,
            priceContext,
            stats,
            access: { canEdit }
        } = usersProfile;

        usersProfiles.push({
            id: String(retargetingId),
            name: retargetingCondition.name,
            userProfileConditions: getUserProfileConditionsForGridRowFromApiFormat(conditionRules, cryptaSegments),
            metrikaAudienceGoals: mapRetargetingConditionRulesForGridRowFromApiFormat(apiConditionRules, goalsMap),
            group: mapRowsToGroupsTruncatedBaseInfo(adGroup, clientInfo, params),
            priceContext: Number(priceContext),
            // TODO: Заменить на флаг canEditPriceContext
            // https://st.yandex-team.ru/DIRECT-99709
            isEditable: canEdit,
            isPriceContextEnabled: getIsPriceContextEnabled(strategy),
            stats: mapStatsDataToStats(stats)
        });

        return usersProfiles;
    }, []);
}

const getGoalsMetrikaOrAudience = (
    conditionRules: UsersProfileQuery_client_retargetings_rowset_retargetingCondition_conditionRules[]
): UsersProfileQuery_client_retargetings_rowset_retargetingCondition_conditionRules[] => {
    return conditionRules.reduce(
        (rules: UsersProfileQuery_client_retargetings_rowset_retargetingCondition_conditionRules[], rule) => {
            const goals = rule.goals.filter(goal => {
                return isGoalMetrikaOrAudience(goal.type);
            });

            if (goals.length) {
                rules.push({ ...rule, goals });
            }

            return rules;
        },
        []
    );
};

export const isGoalMetrikaOrAudience = (goalType: GdGoalType): boolean => {
    return (
        goalType === GdGoalType.GOAL ||
        goalType === GdGoalType.AUDIENCE ||
        goalType === GdGoalType.SEGMENT ||
        goalType === GdGoalType.ECOMMERCE
    );
};

export const getUserProfileConditionsForGridRowFromApiFormat = (
    conditionRules: UsersProfileQuery_client_retargetings_rowset_retargetingCondition_conditionRules[],
    cryptaSegments: CryptaSegment[]
): UserProfileCondition[] => {
    const userProfileConditions: UserProfileCondition[] = [];

    conditionRules.forEach(conditionRule => {
        const { interestType } = conditionRule;
        const conditions: UserProfileCondition = conditionRule.goals.reduce(
            (currentConditions, goal) => {
                const { id, type } = goal;
                let key: string = '';

                // не учитываем сегменты Яндекс.Метрики
                if (isGoalMetrikaOrAudience(type)) {
                    return currentConditions;
                }

                // находим сегмент в справочнике крипты по id
                const cryptaSegment = find(cryptaSegments, { id });

                if (!cryptaSegment) {
                    return currentConditions;
                }

                const { parentId, name } = cryptaSegment;
                const { values } = currentConditions;

                // если уже есть сегменты, то допушиваем
                if (values) {
                    values.push(name);

                    return currentConditions;
                }

                // если сегмента нет, то ищем родительский сегмент,
                // чтобы узнать тип сегмента(пол, возраст и т.д)
                // для интересов родительский сегмент не ищем
                if (!(type === GdGoalType.INTERESTS)) {
                    const parentSegment = find(cryptaSegments, {
                        id: parentId
                    });

                    if (!parentSegment) {
                        return currentConditions;
                    }

                    key = parentSegment.name;
                }

                return {
                    key: key,
                    type: UserProfileRetargetingConditionGoalType[type],
                    values: [name],
                    interestType: interestType ? CryptaInterestTypeWeb[interestType] : null
                };
            },
            {} as UserProfileCondition
        );

        if (isEmpty(conditions)) {
            return;
        }

        userProfileConditions.push(conditions);

        return userProfileConditions;
    });

    return sortBy(userProfileConditions, (condition): number =>
        orderByTypeUserProfileConditions.indexOf(condition.type)
    );
};
